// src/shared/auth/session.tsx
import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getCookie } from '../../lib/http/bffClient';
import { notifyAuthDisconnected } from '@/lib/auth/authDisconnect';
import { isVisitorUser } from '@/shared/auth/isVisitorUser';
import { normalizeAuthUser } from './sessionNormalize';
import { useBrowserPathname } from '@/shared/navigation/useBrowserPathname';

/* ---------- types ---------- */
type NavItem = { path: string; label: string; ability?: string };
type SystemTab = { key: string; label: string };

type Capabilities = Record<string, boolean>;

const OIDC_BRIDGE_COOLDOWN_MS = 15_000;
const OIDC_BRIDGE_RETRY_DELAY_MS = 500;
const OIDC_BRIDGE_MAX_ATTEMPTS = 3;

type MeResponse = {
  authenticated?: boolean;
  user?: any | null;
  user_id?: number;
  id_user?: number;
  email?: string | null;
  linked_providers?: Array<{
    provider_name?: string;
    issuer?: string;
    email_at_link?: string | null;
    linked_at?: string;
  }>;
  capabilities?: Capabilities;
  nav?: NavItem[];           // server-driven sidebar
  systemTabs?: SystemTab[];  // server-driven System Management tabs
  exp?: number;
};

/* ---------- helpers ---------- */

/** Ensure we use a RELATIVE path that CRA will proxy (e.g. '/bff/auth/me'). */
function toRelative(path: string) {
  if (/^https?:\/\//i.test(path)) {
    throw new Error('Use relative /bff/* paths with CRA proxy (Option A).');
  }
  return path.startsWith('/') ? path : `/${path}`;
}

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isGenericIdentityLabel(value: unknown): boolean {
  const v = String(value ?? '').trim().toLowerCase();
  if (!v) return true;
  return v === 'pac account' || v === 'signed in user' || v === 'oidc_user';
}

function isPublicAnonymousPath(pathname: string): boolean {
  const p = String(pathname || '').trim();
  return p.startsWith('/media') || p.startsWith('/s/');
}

function isSharedAnonymousPath(pathname: string): boolean {
  const p = String(pathname || '').trim();
  return p.startsWith('/s/');
}

async function bff<T = any>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const url = toRelative(path);
  const headers = new Headers(init.headers || {});
  const method = (init.method || 'GET').toUpperCase();

  // Add CSRF for non-safe methods. Cookie is set by /bff/auth/login or /bff/auth/me.
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrf = getCookie('csrf');
    if (csrf && !headers.has('X-CSRF')) headers.set('X-CSRF', csrf);
  }
  if (!headers.has('accept')) headers.set('accept', 'application/json');

  const res = await fetch(url, {
    credentials: 'include',
    ...init,
    method,
    headers,
  });

  const contentType = res.headers.get('content-type') || '';
  const text = await res.text().catch(() => '');

  const tryJson = () => {
    try { return text ? JSON.parse(text) : undefined; }
    catch { return undefined; }
  };
  const asJson = contentType.includes('application/json') ? tryJson() : undefined;

  if (!res.ok) {
    const body = asJson ?? text;
    const msg =
      (body && typeof body === 'object' && ('detail' in body || 'error' in body || 'message' in body))
        ? String((body as any).detail ?? (body as any).error ?? (body as any).message)
        : `${method} ${url} → ${res.status} ${res.statusText}`;
    const err: any = new Error(msg);
    err.status = res.status;
    err.body = body;
    throw err;
  }

  return (asJson as T) as T;
}

/* ---------- context ---------- */
type AuthCtx = {
  loading: boolean;
  error: string | null;
  user: any | null;
  capabilities: Capabilities;
  nav: NavItem[];
  systemTabs: SystemTab[];
  /** Session expiration time (epoch ms) returned by /bff/auth/me. */
  exp: number | null;
  /** Re-read the session. Pass `true` to bypass the cooldown after an
   *  explicit auth transition (e.g. immediately after login). */
  refresh: (force?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  can: (cap: string) => boolean;
};

const Ctx = React.createContext<AuthCtx | null>(null);

// immutable defaults
const DEFAULT_MENU: NavItem[] = [];
const DEFAULT_TABS: SystemTab[] = [];
Object.freeze(DEFAULT_MENU);
Object.freeze(DEFAULT_TABS);

export function AuthProvider({
  children,
  fallbackMenu = DEFAULT_MENU,
  fallbackSystemTabs = DEFAULT_TABS,
}: {
  children: React.ReactNode;
  fallbackMenu?: NavItem[];
  fallbackSystemTabs?: SystemTab[];
}) {
  const pathname = useBrowserPathname();
  const [loading, setLoading] = React.useState(true);
  const [error,   setError]   = React.useState<string | null>(null);
  const [user,    setUser]    = React.useState<any | null>(null);
  const [caps,    setCaps]    = React.useState<Capabilities>({});
  const [nav,     setNav]     = React.useState<NavItem[]>(fallbackMenu);
  const [systemTabs, setSystemTabs] = React.useState<SystemTab[]>(fallbackSystemTabs);
  const [exp, setExp] = React.useState<number | null>(null);
  const queryClient = useQueryClient();

  // keep stable refs for fallbacks
  const menuRef = React.useRef<NavItem[]>(fallbackMenu);
  React.useEffect(() => { menuRef.current = fallbackMenu; }, [fallbackMenu]);

  const tabsRef = React.useRef<SystemTab[]>(fallbackSystemTabs);
  React.useEffect(() => { tabsRef.current = fallbackSystemTabs; }, [fallbackSystemTabs]);

  // StrictMode double-invoke guard + de-dupe + cooldown
  const didRunRef   = React.useRef(false);
  const inFlightRef = React.useRef<Promise<void> | null>(null);
  const lastHitRef  = React.useRef(0);
  const lastOidcBridgeAttemptRef = React.useRef(0);
  const prevPublicPathRef = React.useRef<boolean | null>(null);
  const COOLDOWN_MS = 3000;

  const hydrateFromResponse = React.useCallback((m: MeResponse) => {
    const normalized = normalizeAuthUser(m);
    setUser(normalized);
    setCaps(m?.capabilities && typeof m.capabilities === 'object' ? m.capabilities : {});
    setExp(typeof (m as any)?.exp === 'number' ? (m as any).exp : null);

    const serverNav = Array.isArray((m as any)?.nav) ? (m as any).nav as NavItem[] : undefined;
    const isAuthenticated = normalized != null && m?.authenticated !== false;
    const nextNav = serverNav && serverNav.length > 0
      ? serverNav
      : (isAuthenticated ? [] : menuRef.current);
    setNav(nextNav);

    const nextTabs =
      (Array.isArray((m as any)?.systemTabs) ? (m as any).systemTabs : undefined) ??
      (Array.isArray((m as any)?.system_tabs) ? (m as any).system_tabs : undefined) ??
      tabsRef.current;
    setSystemTabs(nextTabs);

    return normalized;
  }, []);

  const tryOidcBridge = React.useCallback(async (): Promise<boolean> => {
    try {
      await bff('/bff/auth/oidc/session/bridge', { method: 'POST' });
      const rawBridge = await bff<unknown>('/bff/auth/me');
      const mBridge = (rawBridge as any)?.data ?? (rawBridge as any)?.result ?? rawBridge as MeResponse;
      hydrateFromResponse(mBridge);
      return true;
    } catch {
      return false;
    }
  }, [hydrateFromResponse]);

  const tryOidcBridgeWithRetry = React.useCallback(async (): Promise<boolean> => {
    for (let attempt = 1; attempt <= OIDC_BRIDGE_MAX_ATTEMPTS; attempt += 1) {
      if (await tryOidcBridge()) return true;
      if (attempt < OIDC_BRIDGE_MAX_ATTEMPTS) {
        await delay(OIDC_BRIDGE_RETRY_DELAY_MS * attempt);
      }
    }
    return false;
  }, [tryOidcBridge]);

  const refresh = React.useCallback(async (force = false) => {
    // `force` is used right after an explicit auth transition (e.g. login)
    // where we MUST re-read /auth/me even if the cooldown window is still
    // open — otherwise the freshly-established session is never reflected
    // in `user` and the SPA bounces straight back to the login screen.
    if (inFlightRef.current) {
      if (!force) return inFlightRef.current;
      try { await inFlightRef.current; } catch { /* fall through to a fresh fetch */ }
    }
    const now = Date.now();
    if (!force && now - lastHitRef.current < COOLDOWN_MS) return;

    setLoading(true); setError(null);
    const p = (async () => {
      const path = (typeof window !== 'undefined' ? window.location.pathname : '') || '';
      const isSharedPublicPath = isSharedAnonymousPath(path);
      const isMediaPublicPath = path.startsWith('/media');
      const isPublicVisitorPath = isPublicAnonymousPath(path);
      try {
        // Shared-link routes are strictly anonymous by contract. Avoid auth/session
        // bootstrap calls there (prevents /auth/me + /auth/guest noise and
        // superadmin tenant-catalog fan-out on public pages).
        if (isSharedPublicPath) {
          setUser(null);
          setCaps({});
          setNav(menuRef.current);
          setSystemTabs(tabsRef.current);
          setExp(null);
          setError(null);
          return;
        }

        // Best-effort: refresh upstream access token + renew session TTL.
        // Skip when no BFF session markers exist yet (e.g. just returned
        // from OIDC and waiting to bridge pac_session -> bffsid), which
        // avoids an expected 401 noise hit on /auth/refresh.
        if (getCookie('csrf')) {
          try {
            await bff('/bff/auth/refresh', { method: 'POST' });
          } catch {
            // ignore transient refresh errors; /me may still succeed
          }
        }

        const raw = await bff<unknown>('/bff/auth/me');
        // tolerate shapes: {..}, {data:{..}}, {result:{..}}
        const m = (raw as any)?.data ?? (raw as any)?.result ?? raw as MeResponse;

        const normalized = hydrateFromResponse(m);

        const shouldPromoteWithOidcBridge = (() => {
          // Promote if identity still looks stale/generic.
          // We cannot rely on reading pac_session from JS because it may be
          // HttpOnly server-side.
          if (!normalized) return true;
          if (isVisitorUser(normalized)) return true;
          const nameLike = (normalized as any)?.display_name ?? (normalized as any)?.name;
          const email = String((normalized as any)?.email ?? '').trim();
          return isGenericIdentityLabel(nameLike) || !email;
        })();

        // When OIDC session evidence exists (pac_session) and the current
        // identity still looks generic/stale, promote via bridge.
        if (shouldPromoteWithOidcBridge && !isPublicVisitorPath) {
          const nowMs = Date.now();
          if (nowMs - lastOidcBridgeAttemptRef.current > OIDC_BRIDGE_COOLDOWN_MS) {
            lastOidcBridgeAttemptRef.current = nowMs;
            try {
              await tryOidcBridgeWithRetry();
            } catch {
              // If bridge fails here, keep the current visitor snapshot.
            }
          }
        }
      } catch (e: any) {
        // If unauthenticated and we are on a public media path, attempt auto-guest
        const status = e?.status ?? (typeof e?.message === 'string' && /401/.test(e.message) ? 401 : undefined);
        const isMediaPublic = isMediaPublicPath;

        // OIDC login uses Rust's pac_session cookie first. Bridge it into a
        // canonical BFF session exactly once per cooldown window before we
        // classify this as unauthenticated.
        if (status === 401 && !isPublicVisitorPath) {
          const nowMs = Date.now();
          if (nowMs - lastOidcBridgeAttemptRef.current > OIDC_BRIDGE_COOLDOWN_MS) {
            lastOidcBridgeAttemptRef.current = nowMs;
            try {
              await tryOidcBridgeWithRetry();
              setError(null);
              return;
            } catch {
              // Fall through to the existing unauthenticated handling.
            }
          }
        }

        // On protected areas, treat 401 as a dropped auth session and trigger the global flow.
        // 403 is a resource-level permission denial — the session is still valid.
        // For public media paths, allow the guest fallback first.
        if (status === 401 && !isMediaPublic) {
          notifyAuthDisconnected({
            reason: 'unauthenticated',
            status,
            url: '/bff/auth/me',
          });
        }
        if (status === 401 && isMediaPublic) {
          try {
            await bff('/bff/auth/guest', { method: 'POST' });
            // Retry me after seeding guest session
            const raw2 = await bff<unknown>('/bff/auth/me');
            const m2 = (raw2 as any)?.data ?? (raw2 as any)?.result ?? raw2 as MeResponse;

            setUser(normalizeAuthUser(m2));
            setCaps(m2?.capabilities && typeof m2.capabilities === 'object' ? m2.capabilities : {});
            setExp(typeof (m2 as any)?.exp === 'number' ? (m2 as any).exp : null);

            const serverNav2 = Array.isArray((m2 as any)?.nav) ? (m2 as any).nav as NavItem[] : undefined;
            const nextNav2 = serverNav2 && serverNav2.length > 0 ? serverNav2 : [];
            setNav(nextNav2);

            const nextTabs2 =
              (Array.isArray((m2 as any)?.systemTabs) ? (m2 as any).systemTabs : undefined) ||
              (Array.isArray((m2 as any)?.system_tabs) ? (m2 as any).system_tabs : undefined) ||
              tabsRef.current;
            setSystemTabs(nextTabs2);
            setError(null);
          } catch (e2: any) {
            // fall back to unauthenticated state
            setUser(null);
            setCaps({});
            setNav(menuRef.current);
            setSystemTabs(tabsRef.current);
            setExp(null);
            setError(e2?.message || 'Not authenticated');
          }
        } else {
          setUser(null);
          setCaps({});
          setNav(menuRef.current);
          setSystemTabs(tabsRef.current);
          setExp(null);
          setError(e?.message || 'Not authenticated');
        }
      } finally {
        lastHitRef.current = Date.now();
        setLoading(false);
      }
    })().finally(() => { inFlightRef.current = null; });

    inFlightRef.current = p;
    return p;
  }, []);

  const logout = React.useCallback(async () => {
    try { await bff('/bff/auth/logout', { method: 'POST' }); } catch {}
    setUser(null);
    setCaps({});
    setNav(menuRef.current);
    setSystemTabs(tabsRef.current);
    setExp(null);
  // Invalidate any cached auth/me data so guarded routes re-check
  try { queryClient.removeQueries({ queryKey: ['me'] }); } catch {}
  }, []);

  React.useEffect(() => {
    if (didRunRef.current) return; // avoid StrictMode double effect
    didRunRef.current = true;
    void refresh();
  }, [refresh]);

  // Ensure shared-route semantics apply even when navigating from an already
  // authenticated SPA session into /s/* without a full reload.
  React.useEffect(() => {
    if (!didRunRef.current) return;
    const isPublicPath = isPublicAnonymousPath(pathname);
    if (prevPublicPathRef.current == null) {
      prevPublicPathRef.current = isPublicPath;
      return;
    }
    if (prevPublicPathRef.current !== isPublicPath) {
      prevPublicPathRef.current = isPublicPath;
      void refresh(true);
    }
  }, [pathname, refresh]);

  const can = React.useCallback((cap: string) => {
    if (!cap) return true;
    return caps.isSuperadmin === true || caps[cap] === true;
  }, [caps]);

  const value: AuthCtx = {
    loading,
    error,
    user,
    capabilities: caps,
    nav,
    systemTabs,
    exp,
    refresh,
    logout,
    can,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function useCan() {
  return useAuth().can;
}
